import { useState, useEffect, useRef } from "react";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";

const GoogleMapsAutocompleteRail = ({
  title = "Location",
  onPlaceSelect,
  placeholder = "Enter location",
  error,
  type = ["geocode"],
  value,
  disable = false,
  titleDisabled = false,
  railMode = false,
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const serviceRef = useRef(null);

  // Get user's current location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value]);

  useEffect(() => {
    if (!window.google) return;

    if (!serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (!inputValue) {
      setOptions([]);
      return undefined;
    }

    if (!serviceRef.current) return undefined;

    setLoading(true);
    const requestTypes = railMode ? ["train_station", "transit_station"] : type;

    // Create request object with location bias if available
    const request = {
      input: inputValue,
      types: requestTypes,
    };

    // Add location bias if we have user's location
    if (userLocation) {
      request.location = new window.google.maps.LatLng(
        userLocation.lat,
        userLocation.lng
      );
      request.radius = 50000; // 50km radius - adjust as needed
      request.strictBounds = false; // Allow results outside the radius but biased towards it
    }

    serviceRef.current.getPlacePredictions(request, (predictions, status) => {
      if (active) {
        setLoading(false);
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          setOptions([]);
          return;
        }

        // Filter for only railway stations and railway junctions
        const filtered = predictions.filter((pred) => {
          const desc = pred.description?.toLowerCase() || "";
          return (
            desc.includes("railway station") ||
            desc.includes("junction") ||
            desc.includes("road") ||
            desc.includes("terminal") ||
            desc.includes("terminus")
          );
        });

        // Sort predictions to prioritize nearby locations
        if (userLocation) {
          filtered.sort((a, b) => {
            return a.distanceMeters - b.distanceMeters;
          });
        }

        setOptions(filtered);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, type, railMode, userLocation]);

  const fetchPlaceDetails = (placeId) => {
    if (!window.google) return;

    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService.getDetails(
      {
        placeId,
        fields: ["name", "formatted_address", "geometry", "address_components"],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          if (onPlaceSelect) {
            const { formatted_address, name, address_components, geometry } =
              place;
            const lat = geometry?.location?.lat();
            const lng = geometry?.location?.lng();
            onPlaceSelect({
              name,
              formatted_address,
              address_components,
              location: {
                lat,
                lng,
              },
            });
          }
        }
      }
    );
  };

  return (
    <Box>
      {!titleDisabled && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mt: 1,
          }}
        >
          {title}
        </Typography>
      )}

      <Autocomplete
        freeSolo
        inputValue={inputValue}
        filterOptions={(x) => x}
        disabled={disable}
        options={options}
        getOptionLabel={(option) => option.description || option.name || ""}
        noOptionsText="No locations"
        loading={loading}
        popupIcon={null}
        value={value ? { description: value } : null}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (newInputValue === "") {
            onPlaceSelect(null);
          }
        }}
        onChange={(event, newValue) => {
          if (newValue?.place_id) {
            fetchPlaceDetails(newValue.place_id);
          } else if (newValue === null) {
            onPlaceSelect(null);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            helperText={
              error && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{
                    marginLeft: "-12px",
                    marginTop: "-5px",
                  }}
                >
                  {!titleDisabled && error}
                </Typography>
              )
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            fullWidth
          />
        )}
      />
    </Box>
  );
};

export default GoogleMapsAutocompleteRail;
